"use client";

import { c as _c } from "react/compiler-runtime";
import CurationEmptyState from "@/components/brain/my-stream/curations/CurationEmptyState";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { Spinner } from "@/components/dotLoader/DotLoader";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import { ApiDropType } from "@/generated/models/ApiDropType";
import CurationDropFooter from "@/components/waves/drops/CurationDropFooter";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import DropMinimalIdentityRow from "@/components/waves/drops/DropMinimalIdentityRow";
import WaveDropContent from "@/components/waves/drops/WaveDropContent";
import WaveDropAuthorPfp from "@/components/waves/drops/WaveDropAuthorPfp";
import WaveDropMetadata from "@/components/waves/drops/WaveDropMetadata";
import WaveDropReply from "@/components/waves/drops/WaveDropReply";
import { ImageScale } from "@/helpers/image.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useCurationManagementPermission } from "@/hooks/useCurationManagementPermission";
import { useDropCurationMembershipMutation } from "@/hooks/drops/useDropCurationMembershipMutation";
import { useNavigateToDropWave } from "@/hooks/useNavigateToDropWave";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Masonry } from "masonic";
import { useCallback, useEffect, useMemo, useState } from "react";
interface UserPageProfileWaveMasonryProps {
  readonly curationId: string;
  readonly curationName?: string | null | undefined;
  readonly containerWidth: number;
  readonly drops: readonly ExtendedDrop[];
  readonly fetchNextPage: () => Promise<void>;
  readonly hasNextPage: boolean | undefined;
  readonly isFetchingNextPage: boolean;
  readonly showIdentity?: boolean | undefined;
  readonly profileIdentity?: ProfileIdentitySummary | undefined;
}
const MASONRY_COLUMN_WIDTH = 300;
const MASONRY_GUTTER = 16;
const noop = () => {};
const CURATION_CARD_CLASS_NAME = "tw-group tw-relative tw-isolate tw-rounded-xl";
const CURATION_CARD_HOVER_FRAME_CLASS_NAME = "tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-rounded-xl tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-out desktop-hover:group-hover:tw-border-white/10 motion-reduce:tw-transition-none";
export type ProfileIdentitySummary = {
  readonly id?: string | null | undefined;
  readonly handle?: string | null | undefined;
  readonly primary_address?: string | null | undefined;
};
type ProfileMasonryIdentityMode = "default" | "minimal" | "hidden";
type ProfileMasonryCardLayout = {
  readonly contentWrapperClassName: string;
  readonly identityMode: ProfileMasonryIdentityMode;
  readonly shouldUseInlineMinimalLayout: boolean;
  readonly showMinimalIdentityRow: boolean;
  readonly usesDefaultDropRenderer: boolean;
};
type ProfileMasonryItem = {
  readonly drop: ExtendedDrop;
  readonly curationId: string;
  readonly canManageActiveCuration: boolean;
  readonly showIdentity: boolean;
  readonly profileIdentity: ProfileIdentitySummary | undefined;
};
const getProfileMasonryCardLayout = ({
  activePart,
  drop,
  profileIdentity,
  replyTo,
  showIdentity
}: {
  readonly activePart: ExtendedDrop["parts"][number] | undefined;
  readonly drop: ExtendedDrop;
  readonly profileIdentity: ProfileIdentitySummary | undefined;
  readonly replyTo: ExtendedDrop["reply_to"];
  readonly showIdentity: boolean;
}): ProfileMasonryCardLayout => {
  const isOwnProfileDrop = areSameProfileIdentity({
    left: drop.author,
    right: profileIdentity
  });
  let identityMode: ProfileMasonryIdentityMode = "default";
  if (!showIdentity) {
    identityMode = isOwnProfileDrop ? "hidden" : "minimal";
  }
  const hasContentPadding = Boolean(replyTo) || Boolean(activePart?.content?.trim()) || Boolean(activePart?.quoted_drop?.drop_id) || (activePart?.media.length ?? 0) === 0;
  const showMinimalIdentityRow = identityMode === "minimal";
  const shouldUseInlineMinimalLayout = showMinimalIdentityRow && Boolean(activePart?.content?.trim()) && !replyTo && !activePart?.quoted_drop?.drop_id && (activePart?.media.length ?? 0) === 0;
  let contentWrapperClassName = "tw-pt-2 tw-pb-2";
  if (hasContentPadding) {
    const topPaddingClass = showMinimalIdentityRow ? "tw-pt-2" : "tw-pt-4";
    contentWrapperClassName = `tw-px-4 ${topPaddingClass} tw-pb-4`;
  } else if (showMinimalIdentityRow) {
    contentWrapperClassName = "tw-pt-1 tw-pb-2";
  }
  return {
    contentWrapperClassName,
    identityMode,
    shouldUseInlineMinimalLayout,
    showMinimalIdentityRow,
    usesDefaultDropRenderer: showIdentity || drop.drop_type !== ApiDropType.Chat
  };
};
export function useProfileMasonryContainerWidth() {
  const $ = _c(6);
  const [container, setContainer] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      setContainer(node);
      setContainerWidth(currentWidth => {
        const nextWidth = node?.offsetWidth ?? 0;
        return currentWidth === nextWidth ? currentWidth : nextWidth;
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const containerRef = t0;
  let t1;
  let t2;
  if ($[1] !== container) {
    t1 = () => {
      if (!container) {
        return;
      }
      const updateWidth = () => {
        const nextWidth_0 = container.offsetWidth;
        setContainerWidth(currentWidth_0 => currentWidth_0 === nextWidth_0 ? currentWidth_0 : nextWidth_0);
      };
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", updateWidth);
        return () => {
          window.removeEventListener("resize", updateWidth);
        };
      }
      const observer = new ResizeObserver(() => {
        updateWidth();
      });
      observer.observe(container);
      return () => {
        observer.disconnect();
      };
    };
    t2 = [container];
    $[1] = container;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== containerWidth) {
    t3 = {
      containerRef,
      containerWidth
    };
    $[4] = containerWidth;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function CurationMasonryRemoveButton(t0) {
  const $ = _c(16);
  const {
    drop,
    curationId
  } = t0;
  const {
    hasTouchScreen,
    isApp
  } = useDeviceInfo();
  const isTouchDevice = useIsTouchDevice();
  let t1;
  if ($[0] !== drop.id) {
    t1 = {
      dropId: drop.id
    };
    $[0] = drop.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    updateMembership,
    isPending
  } = useDropCurationMembershipMutation(t1);
  const shouldAlwaysShow = isTouchDevice || isApp && hasTouchScreen;
  const t2 = shouldAlwaysShow ? "tw-translate-y-0 tw-opacity-100" : "tw-translate-y-[-5px] tw-opacity-0 group-focus-within:tw-translate-y-0 group-focus-within:tw-opacity-100 group-hover:tw-translate-y-0 group-hover:tw-opacity-100";
  let t3;
  if ($[2] !== t2) {
    t3 = ["tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-[1000] tw-transition-all tw-duration-300", t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = t3.join(" ");
  let t5;
  if ($[4] !== curationId || $[5] !== updateMembership) {
    t5 = event => {
      event.stopPropagation();
      updateMembership(curationId, "remove");
    };
    $[4] = curationId;
    $[5] = updateMembership;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isPending) {
    t6 = isPending ? <Spinner dimension={10} /> : <XMarkIcon className="tw-size-5 tw-flex-shrink-0" />;
    $[7] = isPending;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isPending || $[10] !== t5 || $[11] !== t6) {
    t7 = <button type="button" onClick={t5} disabled={isPending} aria-label="Remove drop from this curation" title="Remove from wave" className="tw-pointer-events-auto tw-relative tw-z-[1000] tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-black/50 tw-text-iron-400 tw-shadow-lg tw-backdrop-blur-md tw-transition-all tw-duration-200 hover:tw-border-rose-500/30 hover:tw-bg-rose-500/20 hover:tw-text-rose-300 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-60">{t6}</button>;
    $[9] = isPending;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function UserPageProfileWaveMasonryCard(t0) {
  const $ = _c(36);
  const {
    drop,
    curationId,
    canManageActiveCuration,
    showIdentity,
    profileIdentity
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const replyTo = drop.reply_to;
  const activePart = drop.parts[activePartIndex] ?? drop.parts[0];
  const navigateToDropWave = useNavigateToDropWave();
  let t1;
  if ($[0] !== activePart || $[1] !== drop || $[2] !== profileIdentity || $[3] !== replyTo || $[4] !== showIdentity) {
    t1 = getProfileMasonryCardLayout({
      activePart,
      drop,
      profileIdentity,
      replyTo,
      showIdentity
    });
    $[0] = activePart;
    $[1] = drop;
    $[2] = profileIdentity;
    $[3] = replyTo;
    $[4] = showIdentity;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const layout = t1;
  let t2;
  if ($[6] !== canManageActiveCuration || $[7] !== curationId || $[8] !== drop) {
    t2 = canManageActiveCuration ? <CurationMasonryRemoveButton drop={drop} curationId={curationId} /> : null;
    $[6] = canManageActiveCuration;
    $[7] = curationId;
    $[8] = drop;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const removeButton = t2;
  let t3;
  if ($[10] !== activePartIndex || $[11] !== drop || $[12] !== navigateToDropWave) {
    t3 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onQuoteClick={navigateToDropWave} onLongPress={_temp} setLongPressTriggered={_temp2} onDropContentClick={navigateToDropWave} mediaImageScale={ImageScale.AUTOx1080} fullWidthMedia={true} />;
    $[10] = activePartIndex;
    $[11] = drop;
    $[12] = navigateToDropWave;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const dropContent = t3;
  if (layout.usesDefaultDropRenderer) {
    let t4;
    if ($[14] !== drop) {
      t4 = <CurationDropFooter drop={drop} />;
      $[14] = drop;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== drop || $[17] !== layout.identityMode || $[18] !== navigateToDropWave || $[19] !== t4) {
      t5 = <Drop drop={drop} previousDrop={null} nextDrop={null} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.MY_STREAM} dropViewDropId={null} onReply={noop} onReplyClick={noop} onQuoteClick={navigateToDropWave} onDropContentClick={navigateToDropWave} footer={t4} identityMode={layout.identityMode} timestampLayout="stacked" showInteractions={false} />;
      $[16] = drop;
      $[17] = layout.identityMode;
      $[18] = navigateToDropWave;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    let t6;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div aria-hidden="true" className={CURATION_CARD_HOVER_FRAME_CLASS_NAME} />;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t7;
    if ($[22] !== removeButton || $[23] !== t5) {
      t7 = <article className={CURATION_CARD_CLASS_NAME}>{removeButton}{t5}{t6}</article>;
      $[22] = removeButton;
      $[23] = t5;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  let t4;
  if ($[25] !== drop || $[26] !== dropContent || $[27] !== layout.contentWrapperClassName || $[28] !== layout.shouldUseInlineMinimalLayout || $[29] !== layout.showMinimalIdentityRow || $[30] !== replyTo) {
    t4 = <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950">{layout.shouldUseInlineMinimalLayout ? <div className="tw-flex tw-items-start tw-gap-x-3 tw-px-4 tw-pb-4 tw-pt-4"><WaveDropAuthorPfp drop={drop} /><div className="tw-min-w-0 tw-flex-1"><DropMinimalIdentityRow drop={drop} timestampLayout="stacked" /><div className="tw-mt-2">{dropContent}</div>{drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />}<CurationDropFooter drop={drop} className="tw-mt-3" /></div></div> : <>{layout.showMinimalIdentityRow && <div className="tw-flex tw-items-start tw-gap-x-3 tw-px-4 tw-pt-4"><WaveDropAuthorPfp drop={drop} /><div className="tw-min-w-0 tw-flex-1"><DropMinimalIdentityRow drop={drop} timestampLayout="stacked" /></div></div>}<div className={layout.contentWrapperClassName}>{replyTo && <div className="tw-mb-3"><WaveDropReply dropId={replyTo.drop_id} dropPartId={replyTo.drop_part_id} maybeDrop={replyTo.drop ? {
              ...replyTo.drop,
              wave: drop.wave
            } : null} onReplyClick={_temp3} /></div>}{dropContent}</div>{drop.metadata.length > 0 && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropMetadata metadata={drop.metadata} /></div>}<CurationDropFooter drop={drop} className="tw-px-4 tw-pb-4 tw-pt-2" /></>}</div>;
    $[25] = drop;
    $[26] = dropContent;
    $[27] = layout.contentWrapperClassName;
    $[28] = layout.shouldUseInlineMinimalLayout;
    $[29] = layout.showMinimalIdentityRow;
    $[30] = replyTo;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div aria-hidden="true" className={CURATION_CARD_HOVER_FRAME_CLASS_NAME} />;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== removeButton || $[34] !== t4) {
    t6 = <article className={CURATION_CARD_CLASS_NAME}>{removeButton}{t4}{t5}</article>;
    $[33] = removeButton;
    $[34] = t4;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  return t6;
}
function _temp3(_serialNo) {}
function _temp2(_triggered) {}
function _temp() {}
function UserPageProfileWaveMasonryRenderItem(t0) {
  const $ = _c(6);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.canManageActiveCuration || $[1] !== data.curationId || $[2] !== data.drop || $[3] !== data.profileIdentity || $[4] !== data.showIdentity) {
    t1 = <UserPageProfileWaveMasonryCard drop={data.drop} curationId={data.curationId} canManageActiveCuration={data.canManageActiveCuration} showIdentity={data.showIdentity} profileIdentity={data.profileIdentity} />;
    $[0] = data.canManageActiveCuration;
    $[1] = data.curationId;
    $[2] = data.drop;
    $[3] = data.profileIdentity;
    $[4] = data.showIdentity;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export default function UserPageProfileWaveMasonry(t0) {
  const $ = _c(35);
  const {
    curationId,
    curationName,
    containerWidth,
    drops,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    showIdentity: t1,
    profileIdentity
  } = t0;
  const showIdentity = t1 === undefined ? false : t1;
  const permissionProbeDropId = drops[0]?.id ?? "";
  let t2;
  if ($[0] !== curationId || $[1] !== permissionProbeDropId) {
    t2 = {
      curationId,
      probeDropId: permissionProbeDropId
    };
    $[0] = curationId;
    $[1] = permissionProbeDropId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const canManageActiveCuration = useCurationManagementPermission(t2);
  let t3;
  if ($[3] !== curationName) {
    t3 = curationName?.trim() ?? "Curation";
    $[3] = curationName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const curationTitle = t3;
  let t4;
  if ($[5] !== canManageActiveCuration || $[6] !== curationId || $[7] !== drops || $[8] !== profileIdentity || $[9] !== showIdentity) {
    let t5;
    if ($[11] !== canManageActiveCuration || $[12] !== curationId || $[13] !== profileIdentity || $[14] !== showIdentity) {
      t5 = drop => ({
        drop,
        curationId,
        canManageActiveCuration,
        showIdentity,
        profileIdentity
      });
      $[11] = canManageActiveCuration;
      $[12] = curationId;
      $[13] = profileIdentity;
      $[14] = showIdentity;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = drops.map(t5);
    $[5] = canManageActiveCuration;
    $[6] = curationId;
    $[7] = drops;
    $[8] = profileIdentity;
    $[9] = showIdentity;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const masonryItems = t4;
  let t5;
  if ($[16] !== drops) {
    t5 = drops.slice(0, 8).map(_temp4).join("|");
    $[16] = drops;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const masonryTopItemsKey = t5;
  const masonryKey = `${curationId}-${containerWidth}-${drops.length}-${masonryTopItemsKey}`;
  let t6;
  if ($[18] !== fetchNextPage || $[19] !== hasNextPage || $[20] !== isFetchingNextPage) {
    t6 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp5);
    };
    $[18] = fetchNextPage;
    $[19] = hasNextPage;
    $[20] = isFetchingNextPage;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleBottomIntersection = t6;
  if (drops.length === 0) {
    let t7;
    if ($[22] !== curationTitle) {
      t7 = <CurationEmptyState curationTitle={curationTitle} />;
      $[22] = curationTitle;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    return t7;
  }
  let t7;
  if ($[24] !== containerWidth || $[25] !== masonryItems || $[26] !== masonryKey) {
    t7 = <Masonry key={masonryKey} items={masonryItems} render={UserPageProfileWaveMasonryRenderItem} itemKey={_temp6} itemHeightEstimate={420} columnWidth={MASONRY_COLUMN_WIDTH} columnGutter={MASONRY_GUTTER} rowGutter={MASONRY_GUTTER} overscanBy={2} ssrWidth={containerWidth} ssrHeight={900} />;
    $[24] = containerWidth;
    $[25] = masonryItems;
    $[26] = masonryKey;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== handleBottomIntersection || $[29] !== hasNextPage || $[30] !== isFetchingNextPage) {
    t8 = ((hasNextPage ?? false) || isFetchingNextPage) && <div className="tw-flex tw-justify-center tw-py-6">{isFetchingNextPage ? <CircleLoader size={CircleLoaderSize.MEDIUM} /> : <CommonIntersectionElement onIntersection={handleBottomIntersection} />}</div>;
    $[28] = handleBottomIntersection;
    $[29] = hasNextPage;
    $[30] = isFetchingNextPage;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t7 || $[33] !== t8) {
    t9 = <div className="tw-px-1 tw-pb-2">{t7}{t8}</div>;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}
function _temp6(item) {
  return item.drop.stableKey;
}
function _temp5() {}
function _temp4(drop_0) {
  return drop_0.stableKey;
}